/**
 * «Умный поиск»: из обычной фразы («iPhone 13 до 3 млн в Нукусе, б/у») достаёт фильтры — цену, место, состояние,
 * категорию, «с фото», «торг», — а остальное оставляет текстом запроса. Работает по правилам на трёх языках
 * (русский, узбекский, каракалпакский), без внешних сервисов: быстро, бесплатно и предсказуемо.
 */

export interface SmartNode {
  id: string;
  name: string;
  children: SmartNode[];
}

export interface SmartResult {
  q: string;
  priceMin?: string;
  priceMax?: string;
  condition?: 'new' | 'used';
  locationId?: string;
  locationName?: string;
  categoryId?: string;
  categoryName?: string;
  withPhotos?: boolean;
  negotiable?: boolean;
  /** Что распознано (для подсказки «Поняли так: …»). */
  recognized: ('price' | 'location' | 'condition' | 'category' | 'photos' | 'negotiable')[];
}

const MULT: [RegExp, number][] = [
  [/^(млрд|mlrd|milliard|миллиард\w*)$/i, 1e9],
  [/^(млн|mln|million|миллион\w*|million\w*|millión)$/i, 1e6],
  [/^(тыс|т|k|к|ming|mıń|myń|мың|тысяч\w*|тыщ\w*)$/i, 1e3],
];
const CURRENCY = /^(сум|сумов|сума|so'?m|som|sum|swm|сўм)$/i;
const MAX_WORDS = /^(до|дешевле|не\s*дороже|max|максимум|gacha|ga\s*shekem|shekem|qeder|дейин)$/i;
const MIN_WORDS = /^(от|дороже|min|минимум|dan|nan|den|baslap|boshlab)$/i;
const NEW_WORDS = /^(новый|новая|новое|новые|новую|new|yangi|jańa|jana|жаңа)$/i;
const USED_WORDS = /^(б\/у|бу|б-у|бэу|used|ishlatilgan|islengen|қолданылған|b\/u|bu)$/i;
const PHOTO_WORDS = /^(с\s*фото|фото|rasmli|súwretli|suwretli)$/i;
const TORG_WORDS = /^(торг|торгом|торг\s*уместен|kelishamiz|kelisemiz|kelishiladi|kelisimli)$/i;
const STOP = new Set(['в', 'во', 'на', 'за', 'и', 'с', 'со', 'для', 'сум', 'ищу', 'куплю', 'нужен', 'нужна', 'нужно', 'da', 'de', 'ta', 'kerak', 'kerek', 'izlep', 'qidiraman', 'sotib', 'olaman', 'алам', 'satıp', 'alaman']);

const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е').replace(/[‘’ʻʼ`]/g, "'");
/** Основа слова для сравнения с названиями: первые 5 букв (падежи и аффиксы отбрасываются). */
const stem = (w: string) => norm(w).replace(/[^\p{L}\p{N}]/gu, '').slice(0, 5);
const PLACE_PREFIX = /^(город|г\.|посёлок|поселок|район|shahri|qalası|qalasi|tumani|rayonı)\s+|\s+(shahri|qalası|qalasi|tumani|rayonı|район)$/gi;

function flatten(nodes: SmartNode[], out: SmartNode[] = []): SmartNode[] {
  for (const n of nodes) {
    out.push(n);
    flatten(n.children, out);
  }
  return out;
}

function parseNumber(tok: string): number | null {
  const t = tok.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
}

/** Числа с множителями: «3 млн», «3млн», «500 тыс», «2.5 mln», «1 200 000». */
function readAmount(tokens: string[], i: number): { value: number; used: number } | null {
  let j = i;
  let raw = tokens[j] ?? '';
  // «3млн», «500k» — множитель в том же слове
  const glued = /^(\d+(?:[.,]\d+)?)(млрд|млн|mln|тыс|ming|k|к|т)$/i.exec(raw);
  if (glued) {
    const m = MULT.find(([re]) => re.test(glued[2]!))![1];
    return { value: Number(glued[1]!.replace(',', '.')) * m, used: 1 };
  }
  // «1 200 000» — разряды через пробел
  let digits = parseNumber(raw);
  if (digits === null) return null;
  while (/^\d{3}$/.test(tokens[j + 1] ?? '') && Number.isInteger(digits)) {
    digits = digits * 1000 + Number(tokens[j + 1]);
    j++;
  }
  let used = j - i + 1;
  const next = tokens[j + 1] ?? '';
  const mult = MULT.find(([re]) => re.test(next));
  if (mult) {
    digits *= mult[1];
    used++;
    if (CURRENCY.test(tokens[j + 2] ?? '')) used++;
  } else if (CURRENCY.test(next)) used++;
  return { value: digits, used };
}

export function parseSmartQuery(text: string, opts: { categories?: SmartNode[]; locations?: SmartNode[] } = {}): SmartResult {
  const res: SmartResult = { q: '', recognized: [] };
  // «б/у» и «с фото» — до разбиения на слова
  let src = ` ${norm(text)} `;
  if (/\s(б\/у|б-у|b\/u)\s/i.test(src)) {
    res.condition = 'used';
    src = src.replace(/\s(б\/у|б-у|b\/u)\s/gi, ' ');
  }
  if (/\sс\s+фото\s/.test(src)) {
    res.withPhotos = true;
    src = src.replace(/\sс\s+фото\s/g, ' ');
  }
  if (/\sторг\s+уместен\s/.test(src)) {
    res.negotiable = true;
    src = src.replace(/\sторг\s+уместен\s/g, ' ');
  }
  // «3-5 млн», «от 1 до 3 млн»
  const tokens = src
    .replace(/(\d)\s*[-–—]\s*(\d)/g, '$1 - $2')
    .replace(/[,;!?()]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  const rest: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i]!;
    // «до 3 млн» / «от 500 тыс»
    const priceAfterWord = (MAX_WORDS.test(tok) || MIN_WORDS.test(tok)) ? readAmount(tokens, i + 1) : null;
    // «до 2018» — скорее год, чем цена: ценой считаем от 1000 сум или с множителем («до 3 млн»).
    if (priceAfterWord && priceAfterWord.value >= 1000 && !(priceAfterWord.used === 1 && /^(19|20)\d\d$/.test(tokens[i + 1] ?? ''))) {
      const a = priceAfterWord;
      if (MAX_WORDS.test(tok)) res.priceMax = String(Math.round(a.value));
      else res.priceMin = String(Math.round(a.value));
      i += a.used;
      continue;
    }
    // «3 млн gacha» / «500 ming dan» — служебное слово после суммы (узбекский, каракалпакский)
    const amount = readAmount(tokens, i);
    if (amount) {
      const after = tokens[i + amount.used] ?? '';
      const hasMult = amount.used > 1 || /\D$/.test(tok);
      if (MAX_WORDS.test(after) && hasMult) {
        res.priceMax = String(Math.round(amount.value));
        i += amount.used;
        continue;
      }
      if (MIN_WORDS.test(after) && hasMult) {
        res.priceMin = String(Math.round(amount.value));
        i += amount.used;
        continue;
      }
      // «3 - 5 млн»: множитель второго числа относится и к первому
      if (after === '-') {
        const bIdx = i + amount.used + 1;
        const b = readAmount(tokens, bIdx);
        if (b) {
          const bBase = parseNumber(tokens[bIdx] ?? '') ?? b.value;
          const bMult = bBase ? b.value / bBase : 1;
          res.priceMin = String(Math.round(amount.used === 1 && parseNumber(tok) !== null ? amount.value * bMult : amount.value));
          res.priceMax = String(Math.round(b.value));
          i = bIdx + b.used - 1;
          continue;
        }
      }
      // Число с множителем без слова («iPhone 3 млн») — цена «до»; голое число (модель, год) — текст запроса.
      if (amount.used > 1 && MULT.some(([re]) => re.test(tokens[i + 1] ?? '')) ) {
        res.priceMax = String(Math.round(amount.value));
        i += amount.used - 1;
        continue;
      }
    }
    if (NEW_WORDS.test(tok)) {
      res.condition = 'new';
      continue;
    }
    if (USED_WORDS.test(tok)) {
      res.condition = 'used';
      continue;
    }
    if (TORG_WORDS.test(tok)) {
      res.negotiable = true;
      continue;
    }
    if (PHOTO_WORDS.test(tok) && tok !== 'фото') {
      res.withPhotos = true;
      continue;
    }
    rest.push(tok);
  }

  // Место: слово, совпадающее по основе с названием города/района («в Нукусе», «Nukusda», «Xojeli»).
  const places = flatten(opts.locations ?? []).map((n) => ({ n, s: stem(n.name.replace(PLACE_PREFIX, '').trim()) })).filter((x) => x.s.length >= 4);
  const keep: string[] = [];
  for (let i = 0; i < rest.length; i++) {
    const w = rest[i]!;
    if (!res.locationId && stem(w).length >= 4) {
      const hit = places.find((p) => stem(w) === p.s);
      if (hit) {
        res.locationId = hit.n.id;
        res.locationName = hit.n.name.replace(PLACE_PREFIX, '').trim() || hit.n.name;
        // предлог перед местом («в», «во») — тоже служебный
        if (keep.length && /^(в|во|из)$/.test(keep[keep.length - 1]!)) keep.pop();
        continue;
      }
    }
    keep.push(w);
  }

  // Категория: только явное слово-категория («квартира», «телефон», «машина»), сравнение по основе с названиями.
  const cats = flatten(opts.categories ?? []).flatMap((n) => n.name.split(/[\s,]+/).filter((w) => w.length >= 5).map((w) => ({ n, s: stem(w) })));
  // Слово категории из текста убирается: иначе «телефон» не нашёл бы объявление «iPhone 13» (в нём нет этого слова).
  let catWord = -1;
  keep.forEach((w, idx) => {
    if (res.categoryId || stem(w).length < 5) return;
    const hit = cats.find((c) => c.s === stem(w));
    if (hit) {
      res.categoryId = hit.n.id;
      res.categoryName = hit.n.name;
      catWord = idx;
    }
  });

  res.q = keep.filter((w, idx) => idx !== catWord && !STOP.has(w) && w !== '-').join(' ').trim();
  if (res.priceMin || res.priceMax) res.recognized.push('price');
  if (res.locationId) res.recognized.push('location');
  if (res.condition) res.recognized.push('condition');
  if (res.categoryId) res.recognized.push('category');
  if (res.withPhotos) res.recognized.push('photos');
  if (res.negotiable) res.recognized.push('negotiable');
  if (res.priceMin && res.priceMax && Number(res.priceMin) > Number(res.priceMax)) [res.priceMin, res.priceMax] = [res.priceMax, res.priceMin];
  return res;
}
